import { useState, useEffect } from "react";

interface NetworkInformationEventMap {
  change: Event;
}

interface NetworkInformation extends EventTarget {
  readonly downlink: number;
  readonly effectiveType: string;
  readonly rtt: number;
  readonly saveData: boolean;

  addEventListener<K extends keyof NetworkInformationEventMap>(
    type: K,
    listener: (
      this: NetworkInformation,
      ev: NetworkInformationEventMap[K],
    ) => any,
    options?: boolean | AddEventListenerOptions,
  ): void;

  removeEventListener<K extends keyof NetworkInformationEventMap>(
    type: K,
    listener: (
      this: NetworkInformation,
      ev: NetworkInformationEventMap[K],
    ) => any,
    options?: boolean | EventListenerOptions,
  ): void;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
  }
}

interface NetworkInfo {
  downlink?: number;
  effectiveType?: string;
  rtt?: number;
}

export const useNetworkInformation = (): NetworkInfo | null => {
  const [connection, setConnection] = useState<NetworkInfo | null>(() => {
    if (navigator.connection) {
      return {
        downlink: navigator.connection.downlink,
        effectiveType: navigator.connection.effectiveType,
        rtt: navigator.connection.rtt,
      };
    }

    return null;
  });

  useEffect(() => {
    const conn = navigator.connection;

    if (!conn) return;

    const updateConnection = (() => {
      setConnection({
        downlink: conn.downlink,
        effectiveType: conn.effectiveType,
        rtt: conn.rtt,
      });
    }) as (this: NetworkInformation, ev: Event) => void;

    conn.addEventListener("change", updateConnection);

    return () => {
      conn.removeEventListener("change", updateConnection);
    };
  }, []);

  return connection;
};
